import { DatePicker, Input, Select, Tag, Tooltip } from 'antd';
import { observer } from 'mobx-react';
import moment from 'moment';
import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import * as Component from '../../../../shared/components';
import { TableColumnInterface } from '../../../../shared/type/tableType';
import { formatDateTimePST } from '../../../../shared/utils/timeFormat';
import { kcpInfoService } from '../service/kcpInfoService';
import {
  DeletionType,
  KCP_STATUS_COLORS,
  KCP_TYPE_COLORS,
  KCPContentType,
  KCPInfoItem,
} from '../type/kcpIngestType';

const KcpInfoList: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams({
    page: '1',
    page_size: '20',
    ordering: '-id',
  });
  const [refreshCounter, setRefreshCounter] = useState(0);
  const [_selectedList, setSelectedList] = useState<number[]>([]);

  // Filter states
  const [contentType, setContentType] = useState<string>(
    searchParams.get('content_type') || 'episode'
  );
  const [kcpStatus, setKcpStatus] = useState<string>(searchParams.get('status') || '');
  const [publishStatus, setPublishStatus] = useState<string>(
    searchParams.get('publish_status') || ''
  );
  const [isKocowa, setIsKocowa] = useState<string>(searchParams.get('service_type') || '');
  const [endDateFrom, setEndDateFrom] = useState<string>(searchParams.get('end_date_from') || '');
  const [endDateTo, setEndDateTo] = useState<string>(searchParams.get('end_date_to') || '');
  const [searchQuery, setSearchQuery] = useState<string>(searchParams.get('search') || '');

  // Initialize contentType to 'episode' on first load
  useEffect(() => {
    if (!searchParams.get('content_type')) {
      setContentType('episode');
    }
  }, []);

  useEffect(() => {
    // Update searchParams when filters change
    const newParams = new URLSearchParams(searchParams);

    if (contentType) {
      newParams.set('content_type', contentType);
    } else {
      newParams.delete('content_type');
    }

    if (kcpStatus) {
      newParams.set('status', kcpStatus);
    } else {
      newParams.delete('status');
    }

    if (publishStatus) {
      newParams.set('publish_status', publishStatus);
    } else {
      newParams.delete('publish_status');
    }

    if (isKocowa) {
      newParams.set('service_type', isKocowa);
    } else {
      newParams.delete('service_type');
    }

    if (endDateFrom) {
      newParams.set('end_date_from', endDateFrom);
    } else {
      newParams.delete('end_date_from');
    }

    if (endDateTo) {
      newParams.set('end_date_to', endDateTo);
    } else {
      newParams.delete('end_date_to');
    }

    if (searchQuery) {
      newParams.set('search', searchQuery);
    } else {
      newParams.delete('search');
    }

    newParams.set('page', '1'); // Reset to first page when filters change
    setSearchParams(newParams);
  }, [contentType, kcpStatus, publishStatus, isKocowa, endDateFrom, endDateTo, searchQuery]);

  const columns: TableColumnInterface<KCPInfoItem>[] = [
    {
      title: 'KCP ID',
      dataIndex: 'id',
      key: 'id',
      width: '8%',
      sorter: true,
      render: (_text: string, record: KCPInfoItem): JSX.Element => {
        return <span style={{ fontWeight: 500 }}>{record.id}</span>;
      },
    },
    {
      title: 'Type',
      dataIndex: 'content_type',
      key: 'content_type',
      width: '7%',
      render: (_: string, record: KCPInfoItem): JSX.Element => {
        return (
          <Tag color={KCP_TYPE_COLORS[record.content_type] || 'default'}>
            {record.content_type?.toUpperCase()}
          </Tag>
        );
      },
    },
    {
      title: 'Title',
      dataIndex: 'title',
      key: 'title',
      width: '15%',
      render: (_: string, record: KCPInfoItem): JSX.Element => (
        <div style={{ wordBreak: 'break-word', fontWeight: 500 }}>{record.title}</div>
      ),
    },
    {
      title: 'MediaHub ID',
      dataIndex: 'mediahub',
      key: 'mediahub_id',
      width: '7%',
      render: (_: string, record: KCPInfoItem): JSX.Element => {
        if (!record.mediahub) {
          return <span style={{ color: '#999' }}>-</span>;
        }
        const linkPath =
          record.mediahub.content_type === 'season'
            ? `/season/edit2/${record.mediahub.id}`
            : `/episode/edit/${record.mediahub.id}`;
        return (
          <Link
            to={linkPath}
            style={{ fontWeight: 500, color: '#1890ff' }}
            onClick={e => e.stopPropagation()}
          >
            {record.mediahub.id}
          </Link>
        );
      },
    },
    {
      title: 'MediaHub Slug',
      dataIndex: 'mediahub',
      key: 'mediahub_slug',
      width: '15%',
      render: (_: string, record: KCPInfoItem): JSX.Element => {
        if (!record.mediahub) {
          return <span style={{ color: '#999' }}>-</span>;
        }
        const linkPath =
          record.mediahub.content_type === 'season'
            ? `/season/edit2/${record.mediahub.id}`
            : `/episode/edit/${record.mediahub.id}`;
        return (
          <Link
            to={linkPath}
            style={{ color: '#1890ff', wordBreak: 'break-word' }}
            onClick={e => e.stopPropagation()}
          >
            {record.mediahub.slug}
          </Link>
        );
      },
    },
    {
      title: 'Publish Status',
      dataIndex: 'mediahub',
      key: 'publish_status',
      width: '10%',
      render: (_: string, record: KCPInfoItem): JSX.Element => {
        if (!record.mediahub) {
          return <span style={{ color: '#999' }}>-</span>;
        }
        return <span>{record.mediahub.publish_status}</span>;
      },
    },
    {
      title: 'File Path',
      dataIndex: 'file_path',
      key: 'file_path',
      width: '15%',
      render: (_text: string, record: KCPInfoItem): JSX.Element => (
        <a
          href="#"
          onClick={e => {
            e.preventDefault();
            navigate(`/kcp-ingest/detail/${record.content_ingest_id}`);
          }}
          style={{ color: '#1890ff', minWidth: 150, wordBreak: 'break-word' }}
        >
          {record.file_path}
        </a>
      ),
    },
    {
      title: 'KCP Status',
      dataIndex: 'status',
      key: 'status',
      width: '8%',
      render: (_: string, record: KCPInfoItem): JSX.Element => {
        return (
          <Tag color={KCP_STATUS_COLORS[record.status] || 'default'}>
            {record.status?.toUpperCase()}
          </Tag>
        );
      },
    },
    {
      title: 'Is Kocowa',
      dataIndex: 'is_kocowa',
      key: 'is_kocowa',
      width: '7%',
      render: (_: string, record: KCPInfoItem): JSX.Element => {
        return <span style={{ fontWeight: 500 }}>{record.is_kocowa ? 'O' : 'X'}</span>;
      },
    },
    {
      title: 'Start Date',
      dataIndex: 'start_date',
      key: 'start_date',
      width: '10%',
      render: (_: string, record: KCPInfoItem): string => {
        return formatDateTimePST(record.start_date);
      },
    },
    {
      title: 'End Date',
      dataIndex: 'end_date',
      key: 'end_date',
      width: '10%',
      render: (_: string, record: KCPInfoItem): string => {
        return formatDateTimePST(record.end_date);
      },
    },
    {
      title: 'Created / Modified',
      dataIndex: 'created',
      key: 'created_modified',
      width: '12%',
      render: (_text: string, record: KCPInfoItem): JSX.Element => (
        <>
          <div>{formatDateTimePST(record.created)}</div>
          <div style={{ fontSize: '12px', color: '#666' }}>
            {formatDateTimePST(record.modified)}
          </div>
        </>
      ),
    },
  ];

  const fetchData = useCallback(
    async (params: URLSearchParams): Promise<{ data: KCPInfoItem[]; count: number }> => {
      try {
        const result = await kcpInfoService.fetch(params);
        console.log('=== KCP Info API Response ===');
        console.log('Count:', result.count);
        console.log('First item (전체):', JSON.stringify(result.results[0], null, 2));
        console.log('All fields:', Object.keys(result.results[0] || {}));
        return { data: result.results, count: result.count };
      } catch (error) {
        console.error('Failed to fetch KCP info list:', error);
        return { data: [], count: 0 };
      }
    },
    []
  );

  useEffect(() => {
    setRefreshCounter(prev => prev + 1);
  }, [searchParams]);

  return (
    <Component.BasePageComponent>
      <div style={{ marginBottom: 16, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <Tooltip
          title={
            <div>
              검색 가능 항목:
              <br />• MediaHub Slug
              <br />• Title (KCP 제목)
              <br />• Title KO (KCP 한국어 제목)
              <br />• MediaHub ID
            </div>
          }
        >
          <Input.Search
            placeholder="Search MediaHub Slug, Title, Title KO, MediaHub ID"
            allowClear
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            onSearch={value => setSearchQuery(value)}
            style={{ width: 350 }}
            enterButton
          />
        </Tooltip>

        <Select
          style={{ width: 200 }}
          placeholder="Type: Episode"
          value={contentType}
          onChange={value => setContentType(value)}
        >
          <Select.Option value={KCPContentType.SERIES}>Series</Select.Option>
          <Select.Option value={KCPContentType.SEASON}>Season</Select.Option>
          <Select.Option value={KCPContentType.EPISODE}>Episode</Select.Option>
        </Select>

        <Select
          style={{ width: 200 }}
          placeholder="KCP Status: All"
          allowClear
          value={kcpStatus || undefined}
          onChange={value => setKcpStatus(value || '')}
        >
          <Select.Option value={DeletionType.ACTIVE}>Active</Select.Option>
          <Select.Option value={DeletionType.INACTIVE}>Inactive</Select.Option>
          <Select.Option value={DeletionType.DELETE}>Delete</Select.Option>
        </Select>

        <Select
          style={{ width: 200 }}
          placeholder="Publish Status: All"
          allowClear
          value={publishStatus || undefined}
          onChange={value => setPublishStatus(value || '')}
        >
          <Select.Option value="published">Published</Select.Option>
          <Select.Option value="draft">Draft</Select.Option>
          <Select.Option value="unpublished">Unpublished</Select.Option>
        </Select>

        <Select
          style={{ width: 200 }}
          placeholder="Is Kocowa: All"
          allowClear
          value={isKocowa || undefined}
          onChange={value => setIsKocowa(value || '')}
        >
          <Select.Option value="kocowa">Kocowa</Select.Option>
          <Select.Option value="none">None</Select.Option>
        </Select>

        <DatePicker
          style={{ width: 200 }}
          placeholder="End Date From"
          value={endDateFrom ? moment(endDateFrom) : undefined}
          onChange={(date, dateString) => setEndDateFrom(dateString as string)}
        />

        <DatePicker
          style={{ width: 200 }}
          placeholder="End Date To"
          value={endDateTo ? moment(endDateTo) : undefined}
          onChange={(date, dateString) => setEndDateTo(dateString as string)}
        />
      </div>

      <Component.TableComponent<KCPInfoItem>
        key={refreshCounter}
        name="KCP Info List"
        searchParams={searchParams}
        setSearchParams={setSearchParams}
        dataFetch={fetchData}
        columns={columns}
        options={{
          isDisableIdColumn: true,
        }}
        actions={{
          onSelectionChange: setSelectedList,
        }}
      />
    </Component.BasePageComponent>
  );
};

export default observer(KcpInfoList);
